import { useCallback, useEffect, useState } from "react";
import { Check, X, Pencil, MessageSquareReply, Trash2, Search, Package, Store, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Stars } from "@/components/Stars";
import { ReviewStatusBadge } from "@/components/StatusBadge";
import { EditReviewDialog, ReplyDialog } from "@/components/admin/ReviewDialogs";
import { MediaGallery } from "@/components/Media";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const Row = ({ r, selected, onSelect, act }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 flex gap-4" data-testid={`admin-review-${r.id}`}>
    <Checkbox checked={selected} onCheckedChange={onSelect} className="mt-1" data-testid={`admin-review-select-${r.id}`} />
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Stars value={r.rating} size={15} />
        <span className="font-semibold text-sm">{r.author_name}</span>
        <ReviewStatusBadge status={r.status} />
        <span className="inline-flex items-center gap-1 text-xs text-gray-600">{r.type === "shop" ? <><Store size={12} /> Negozio</> : <><Package size={12} /> {r.product_name}</>}</span>
        {r.edited && <span className="inline-flex items-center gap-1 text-xs text-gray-500"><PenLine size={12} /> modificata</span>}
        <span className="text-xs text-gray-500 ml-auto">#{r.order_number} · {fmtDateTime(r.created_at)}</span>
      </div>
      {r.title && <p className="mt-2 font-semibold">{r.title}</p>}
      <p className="mt-1 text-sm text-gray-700 whitespace-pre-line">{r.comment || <em className="text-gray-400">Nessun testo</em>}</p>
      <MediaGallery media={r.media} size="h-16 w-16" onDelete={(m) => act.delMedia(r, m)} />
      {r.reply?.text && <p className="mt-3 text-sm border-l-2 border-emerald-900 pl-3 text-gray-600"><b className="text-emerald-900">Risposta:</b> {r.reply.text}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {r.status !== "approved" && <Button size="sm" className="bg-emerald-900 hover:bg-emerald-800 h-8" onClick={() => act.status(r, "approved")} data-testid={`approve-review-${r.id}`}><Check size={14} /> Accetta</Button>}
        {r.status !== "rejected" && <Button size="sm" variant="outline" className="h-8 text-red-700 border-red-200 hover:bg-red-50" onClick={() => act.status(r, "rejected")} data-testid={`reject-review-${r.id}`}><X size={14} /> Rifiuta</Button>}
        <Button size="sm" variant="outline" className="h-8" onClick={() => act.edit(r)} data-testid={`edit-review-${r.id}`}><Pencil size={14} /> Modifica</Button>
        <Button size="sm" variant="outline" className="h-8" onClick={() => act.reply(r)} data-testid={`reply-review-${r.id}`}><MessageSquareReply size={14} /> Rispondi</Button>
        <Button size="sm" variant="ghost" className="h-8 text-gray-500" onClick={() => act.del(r)} data-testid={`delete-review-${r.id}`}><Trash2 size={14} /></Button>
      </div>
    </div>
  </div>
);

export default function Reviews() {
  const [status, setStatus] = useState("pending");
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [sel, setSel] = useState([]);
  const [editing, setEditing] = useState(null);
  const [replying, setReplying] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    const params = { limit: 100, ...(status !== "all" && { status }), ...(type !== "all" && { type }), ...(q && { q }) };
    const { data } = await api.get("/admin/reviews", { params });
    setItems(data.items); setTotal(data.total); setSel([]);
  }, [status, type, q]);
  useEffect(() => { load(); }, [load]);

  const replace = (r) => setItems((xs) => xs.map((x) => (x.id === r.id ? r : x)));
  const act = {
    status: async (r, s) => {
      try { await api.patch(`/admin/reviews/${r.id}`, { status: s }); toast.success(s === "approved" ? "Recensione pubblicata" : "Recensione rifiutata"); load(); }
      catch (e) { toast.error(errMsg(e)); }
    },
    edit: setEditing, reply: setReplying, del: setDeleting,
    delMedia: async (r, m) => {
      if (!window.confirm("Eliminare questo file dalla recensione?")) return;
      try { const { data } = await api.delete(`/admin/reviews/${r.id}/media/${m.id}`); replace(data); toast.success("File eliminato"); }
      catch (e) { toast.error(errMsg(e)); }
    },
  };
  const bulk = async (s) => {
    await api.post("/admin/reviews/bulk", { ids: sel, status: s });
    toast.success(`${sel.length} recensioni aggiornate`); load();
  };
  const confirmDelete = async () => {
    await api.delete(`/admin/reviews/${deleting.id}`); toast.success("Recensione eliminata"); setDeleting(null); load();
  };

  return (
    <div className="fade-up">
      <PageHeader title="Recensioni" sub={`${total} risultati`} />
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between mb-4">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="filter-status-pending">In attesa</TabsTrigger>
            <TabsTrigger value="approved" data-testid="filter-status-approved">Pubblicate</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="filter-status-rejected">Rifiutate</TabsTrigger>
            <TabsTrigger value="all" data-testid="filter-status-all">Tutte</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-36 bg-white" data-testid="filter-type-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti i tipi</SelectItem><SelectItem value="shop">Negozio</SelectItem><SelectItem value="product">Prodotti</SelectItem></SelectContent>
          </Select>
          <div className="relative"><Search size={15} className="absolute left-3 top-2.5 text-gray-400" />
            <Input className="pl-9 w-56 bg-white" placeholder="Cerca testo, autore, ordine" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reviews-search-input" /></div>
        </div>
      </div>
      {sel.length > 0 && (
        <div className="sticky top-16 lg:top-2 z-20 mb-4 flex items-center gap-3 bg-emerald-900 text-white rounded-lg px-4 py-2.5" data-testid="bulk-actions-bar">
          <span className="text-sm">{sel.length} selezionate</span>
          <Button size="sm" variant="secondary" className="h-8" onClick={() => bulk("approved")} data-testid="bulk-approve-button">Accetta tutte</Button>
          <Button size="sm" variant="secondary" className="h-8" onClick={() => bulk("rejected")} data-testid="bulk-reject-button">Rifiuta tutte</Button>
        </div>
      )}
      <div className="space-y-3">
        {items.map((r) => (
          <Row key={r.id} r={r} act={act} selected={sel.includes(r.id)} onSelect={(v) => setSel((s) => (v ? [...s, r.id] : s.filter((x) => x !== r.id)))} />
        ))}
        {!items.length && <div className="bg-white border border-dashed border-gray-300 rounded-lg p-12 text-center text-sm text-gray-600" data-testid="admin-reviews-empty">Nessuna recensione in questa vista.</div>}
      </div>
      <EditReviewDialog review={editing} onClose={() => setEditing(null)} onSaved={replace} />
      <ReplyDialog review={replying} onClose={() => setReplying(null)} onSaved={replace} />
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Eliminare la recensione?</AlertDialogTitle><AlertDialogDescription>L'operazione è definitiva. In alternativa puoi rifiutarla.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="delete-cancel">Annulla</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete} data-testid="delete-confirm">Elimina</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
